import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Plus, FileDiff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { changeOrdersApi, projectsApi } from '../api';
import { centsToDollarString, fmtDate } from '../constants';
import { CO_STATUS_OPTIONS, CO_STATUS_STYLES, coStatusLabel, coActivity, signedMoney } from '../quoteConstants';

const selectCls = 'bg-[#1A1A1A] border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none';
const inputCls = 'bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37]';
const labelCls = 'text-xs text-gray-400 mb-1 block';
const money = (c) => (c == null ? '—' : centsToDollarString(c));

export const ChangeOrderCreateDialog = ({ open, onOpenChange, fixedProjectId }) => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ project_id: '', title: '', change_order_date: new Date().toISOString().slice(0, 10), valid_until: '', default_price_level: '', scope_summary: '', reason_for_change: '', exclusions: '', terms: '', internal_notes: '' });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...f, project_id: fixedProjectId || '', title: '' }));
      projectsApi.list({}).then((r) => setProjects(r.data.filter((p) => !p.is_archived && ['planning', 'scheduled', 'in_progress', 'on_hold'].includes(p.status)))).catch(() => {});
    }
  }, [open, fixedProjectId]);

  const submit = async () => {
    if (!form.project_id) return toast.error('Project is required');
    if (!form.title.trim()) return toast.error('Title is required');
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === '' ? null : v]));
      const r = await changeOrdersApi.create(payload);
      toast.success('Change order created');
      onOpenChange(false);
      navigate(`/ops/change-orders/${r.data.id}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not create change order');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="bg-[#111111] border-gray-800 text-white max-w-xl max-h-[90vh] overflow-y-auto" data-testid="co-create-dialog">
        <DialogHeader><DialogTitle>New Customer Change Order</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className={labelCls}>Project *</label>
            <select value={form.project_id} onChange={set('project_id')} disabled={!!fixedProjectId} className={`${selectCls} w-full`} data-testid="co-project-select">
              <option value="">Select a project…</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div><label className={labelCls}>Title *</label><Input value={form.title} onChange={set('title')} placeholder="e.g. Add basement bathroom" className={inputCls} data-testid="co-title-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Date</label><Input type="date" value={form.change_order_date} onChange={set('change_order_date')} className={inputCls} data-testid="co-date-input" /></div>
            <div><label className={labelCls}>Valid Until</label><Input type="date" value={form.valid_until} onChange={set('valid_until')} className={inputCls} data-testid="co-valid-until-input" /></div>
          </div>
          <div><label className={labelCls}>Scope Summary</label><Textarea value={form.scope_summary} onChange={set('scope_summary')} rows={2} className={inputCls} data-testid="co-scope-input" /></div>
          <div><label className={labelCls}>Reason for Change</label><Textarea value={form.reason_for_change} onChange={set('reason_for_change')} rows={2} className={inputCls} data-testid="co-reason-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Exclusions</label><Textarea value={form.exclusions} onChange={set('exclusions')} rows={2} className={inputCls} data-testid="co-exclusions-input" /></div>
            <div><label className={labelCls}>Terms</label><Textarea value={form.terms} onChange={set('terms')} rows={2} className={inputCls} data-testid="co-terms-input" /></div>
          </div>
          <div><label className={labelCls}>Internal Notes</label><Textarea value={form.internal_notes} onChange={set('internal_notes')} rows={2} className={inputCls} data-testid="co-notes-input" /></div>
          <p className="text-xs text-gray-500">Customer and job address are prefilled from the Project and can be corrected while Draft.</p>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="border-gray-700 text-gray-300" data-testid="co-create-cancel-btn">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="co-create-save-btn">
              {saving && <Loader2 size={14} className="animate-spin" />} Create Change Order
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function OpsChangeOrders() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [cos, setCos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ search: '', status: '', expired: '', archived: false, sort: 'recently_updated' });
  const [createOpen, setCreateOpen] = useState(searchParams.get('new') === '1');

  const load = useCallback(() => {
    setLoading(true);
    const p = { archived: f.archived, sort: f.sort };
    if (f.search.trim()) p.search = f.search.trim();
    if (f.status) p.status = f.status;
    if (f.expired) p.expired = f.expired === 'yes';
    changeOrdersApi.list(p).then((r) => setCos(r.data)).catch(() => toast.error('Could not load change orders')).finally(() => setLoading(false));
  }, [f]);
  useEffect(() => { load(); }, [load]);

  const approved = cos.filter((c) => c.status === 'approved');
  const summary = {
    draft: cos.filter((c) => c.status === 'draft').length,
    awaiting: cos.filter((c) => c.status === 'sent').length,
    approvedNet: approved.reduce((s, c) => s + (c.totals.net_customer_change_cents || 0), 0),
    projectsWithApproved: new Set(approved.map((c) => c.project_id)).size,
  };

  return (
    <div data-testid="ops-change-orders-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-white">Customer Change Orders</h2>
          <p className="text-gray-400 mt-1">Track customer-approved additions and deductions to Project scope and Contract Value.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="new-co-btn">
          <Plus size={16} /> New Change Order
        </Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Card className="bg-[#111111] border-gray-800 p-4" data-testid="co-sum-draft"><div className="text-xs text-gray-500">Draft</div><div className="text-xl font-bold text-white mt-1">{summary.draft}</div></Card>
        <Card className="bg-[#111111] border-gray-800 p-4" data-testid="co-sum-awaiting"><div className="text-xs text-gray-500">Awaiting Response</div><div className="text-xl font-bold text-blue-400 mt-1">{summary.awaiting}</div></Card>
        <Card className="bg-[#111111] border-gray-800 p-4" data-testid="co-sum-approved-net"><div className="text-xs text-gray-500">Approved Net Change</div><div className={`text-xl font-bold mt-1 ${summary.approvedNet < 0 ? 'text-red-400' : 'text-green-400'}`}>{signedMoney(summary.approvedNet)}</div></Card>
        <Card className="bg-[#111111] border-gray-800 p-4" data-testid="co-sum-projects"><div className="text-xs text-gray-500">Projects With Approved Changes</div><div className="text-xl font-bold text-[#D4AF37] mt-1">{summary.projectsWithApproved}</div></Card>
      </div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <Input value={f.search} onChange={(e) => setF((x) => ({ ...x, search: e.target.value }))} placeholder="Search change orders…" className={`${inputCls} w-full sm:w-56`} data-testid="co-search" />
        <select value={f.status} onChange={(e) => setF((x) => ({ ...x, status: e.target.value }))} className={selectCls} data-testid="co-status-filter">
          <option value="">All statuses</option>
          {CO_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={f.expired} onChange={(e) => setF((x) => ({ ...x, expired: e.target.value }))} className={selectCls} data-testid="co-expired-filter">
          <option value="">Expired: any</option><option value="yes">Expired only</option><option value="no">Not expired</option>
        </select>
        <select value={f.sort} onChange={(e) => setF((x) => ({ ...x, sort: e.target.value }))} className={selectCls} data-testid="co-sort">
          <option value="recently_updated">Recently Updated</option><option value="newest_date">Newest Date</option>
          <option value="oldest_date">Oldest Date</option><option value="highest_change">Highest Positive Change</option>
          <option value="largest_deduction">Largest Deduction</option><option value="project_az">Project A–Z</option>
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer pb-2">
          <input type="checkbox" checked={f.archived} onChange={(e) => setF((x) => ({ ...x, archived: e.target.checked }))} data-testid="co-archived-toggle" /> Archived
        </label>
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#D4AF37]" size={28} /></div>
        : cos.length === 0 ? (
          <Card className="bg-[#111111] border-gray-800 p-10 text-center" data-testid="co-empty-state">
            <FileDiff size={36} className="mx-auto text-gray-600 mb-3" />
            <p className="text-gray-400">No change orders yet.</p>
          </Card>
        ) : (
          <>
            <div className="hidden lg:block overflow-x-auto rounded-lg border border-gray-800">
              <table className="w-full text-sm" data-testid="co-table">
                <thead className="bg-black text-[#D4AF37] text-[10px] uppercase tracking-wider font-brand"><tr>
                  <th className="text-left px-4 py-3">Change Order</th><th className="text-left px-4 py-3">Project</th>
                  <th className="text-left px-4 py-3">Customer</th><th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Additions</th>
                  <th className="text-right px-4 py-3">Deductions</th><th className="text-right px-4 py-3">Net Change</th>
                  <th className="text-left px-4 py-3">Customer Activity</th><th className="text-left px-4 py-3">Updated</th>
                </tr></thead>
                <tbody>
                  {cos.map((c) => {
                    const act = coActivity(c);
                    return (
                      <tr key={c.id} onClick={() => navigate(`/ops/change-orders/${c.id}`)} className="border-t border-gray-800 hover:bg-[#161616] cursor-pointer" data-testid={`co-row-${c.id}`}>
                        <td className="px-4 py-3"><div className="text-white font-medium">{c.title}</div><div className="text-xs text-gray-500">{c.change_order_number}</div></td>
                        <td className="px-4 py-3 text-gray-300">{c.project_summary.name}</td>
                        <td className="px-4 py-3 text-gray-400">{c.customer_name}</td>
                        <td className="px-4 py-3 text-gray-400">{fmtDate(c.change_order_date)}</td>
                        <td className="px-4 py-3"><span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${CO_STATUS_STYLES[c.status]}`}>{coStatusLabel(c.status)}</span>{c.is_expired && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full border bg-amber-900/30 text-amber-400 border-amber-800">Expired</span>}</td>
                        <td className="px-4 py-3 text-right text-gray-300">{money(c.totals.addition_customer_total_cents)}</td>
                        <td className="px-4 py-3 text-right text-gray-300">{c.totals.deduction_customer_total_cents ? `-${centsToDollarString(c.totals.deduction_customer_total_cents)}` : money(c.totals.deduction_customer_total_cents)}</td>
                        <td className={`px-4 py-3 text-right font-medium ${(c.totals.net_customer_change_cents || 0) < 0 ? 'text-red-400' : 'text-green-400'}`}>{signedMoney(c.totals.net_customer_change_cents)}</td>
                        <td className={`px-4 py-3 text-xs ${act.cls}`}>{act.label}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{fmtDate(c.updated_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="lg:hidden space-y-3">
              {cos.map((c) => {
                const act = coActivity(c);
                return (
                  <Card key={c.id} onClick={() => navigate(`/ops/change-orders/${c.id}`)} className="bg-[#111111] border-gray-800 p-4 cursor-pointer" data-testid={`co-card-${c.id}`}>
                    <div className="flex justify-between items-start gap-2">
                      <div><div className="text-white font-medium">{c.title}</div><div className="text-xs text-gray-500">{c.change_order_number} · {c.project_summary.name}</div></div>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${CO_STATUS_STYLES[c.status]}`}>{coStatusLabel(c.status)}</span>
                    </div>
                    <div className="flex justify-between items-center mt-3 text-sm">
                      <span className={`text-xs ${act.cls}`}>{act.label}</span>
                      <span className={`font-medium ${(c.totals.net_customer_change_cents || 0) < 0 ? 'text-red-400' : 'text-green-400'}`}>{signedMoney(c.totals.net_customer_change_cents)}</span>
                    </div>
                  </Card>
                );
              })}
            </div>
          </>
        )}
      <ChangeOrderCreateDialog open={createOpen} onOpenChange={setCreateOpen} fixedProjectId={searchParams.get('project') || undefined} />
    </div>
  );
}
